import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'QIndex One';
const BASE_URL = 'https://qindexone.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

// Page-specific SEO configurations
export const SEO_CONFIG = {
  home: {
    title: 'QIndex One | Premium Client 360 Platform for Family Offices',
    description: 'QIndex One is a premium client information operating layer for family offices and advisers. Organise, permission-control, and verify client data across your organisation stack.',
    keywords: 'family office software, client 360, wealth management platform, family office technology, client information management, multi-family office software, wealth administration',
    type: 'website'
  },
  login: {
    title: 'Sign In | QIndex One',
    description: 'Sign in to QIndex One to access your Client 360 dashboard. Secure login for family office professionals.',
    keywords: 'QIndex One login, client 360 sign in, family office portal',
    type: 'website'
  },
  pilot: {
    title: 'Request a Pilot | QIndex One',
    description: 'Request a pilot of QIndex One. Experience premium client management for your family office or advisory firm.',
    keywords: 'QIndex One pilot, family office demo, wealth management trial',
    type: 'website'
  },
  contact: {
    title: 'Contact Us | QIndex One',
    description: 'Get in touch with QIndex One. Schedule a walkthrough or learn more about our Client 360 platform.',
    keywords: 'contact QIndex One, family office consultation, wealth platform demo',
    type: 'website'
  },
  security: {
    title: 'Security & Compliance | QIndex One',
    description: 'Learn about QIndex One security controls, ISO 27001 compliance, and data protection policies.',
    keywords: 'QIndex One security, family office compliance, data protection, ISO 27001',
    type: 'website'
  },
  dashboard: {
    title: 'Dashboard | QIndex One',
    description: 'Manage your client portfolio with QIndex One. View family groups, leads, and analytics.',
    keywords: 'client dashboard, family office management, portfolio overview',
    type: 'website'
  }
};

export default function SEO({ 
  page = 'home',
  title,
  description,
  keywords,
  image,
  url,
  type,
  structuredData,
  noindex = false
}) {
  const config = SEO_CONFIG[page] || SEO_CONFIG.home;
  
  const pageTitle = title || config.title;
  const pageDescription = description || config.description;
  const pageKeywords = keywords || config.keywords;
  const pageImage = image || DEFAULT_IMAGE;
  const pageUrl = url || `${BASE_URL}/${page === 'home' ? '' : page}`;
  const pageType = type || config.type;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={pageType} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_AU" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Additional Meta */}
      <meta name="author" content="QIndex One" />
      <meta name="geo.region" content="AU" />
      <meta name="geo.placename" content="Australia" />
      <meta name="language" content="en-AU" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

// Pre-built structured data schemas
export const StructuredData = {
  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "QIndex One",
    "url": BASE_URL,
    "logo": `${BASE_URL}/logo.png`,
    "description": "Premium Client 360 Platform for Family Offices and Advisers",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "AU"
    },
    "sameAs": [
      "https://www.linkedin.com/company/qindexone"
    ]
  },
  
  softwareApplication: {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "QIndex One",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "2495",
      "priceCurrency": "AUD"
    },
    "description": "Client information operating layer for family offices that organises, permission-controls, and verifies client data."
  },
  
  website: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "QIndex One",
    "url": BASE_URL,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${BASE_URL}/app/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  },
  
  faqPage: (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  }),
  
  breadcrumb: (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  })
};
